import React, { useContext, useMemo, useRef, useState } from 'react'
import { KeyboardAvoidingView, Platform, Text, TextInput, View } from 'react-native'
import { ThemeContext } from '../utility/context/ThemeContext'
import { textStyles } from '../style'
import { ThemedButton } from '../utility/input/buttons'
import PhoneNumberInput from '../utility/phoneNumberInput'
import MFAInput from '../utility/mfa-input'
import api from '../api-client'
import { COUNTRY_CODES, PHONE_REGEX } from '@ecountabl/lib'
import { StackActions, useNavigation } from '@react-navigation/native'
import { DismissKeyboard } from '../utility'
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons'
import ThemedDialog from '../style/dialog'
import Analytics from '../utility/analytics'

const ManageMfa = (): JSX.Element => {
  const theme = useContext(ThemeContext).theme
  const { textMain, success, bgMain } = theme.colors
  const inputRef = useRef<TextInput>(null)

  const usCountry = useMemo(() => COUNTRY_CODES.find(c => c.code === 'US'), [])
  const [countryCode, setCountryCode] = useState<string>(`${usCountry?.code} ${usCountry?.dial_code}`)
  const [phone, setPhone] = useState<string>("")
  const [sending, setSending] = useState<boolean>(false)
  const [error, setError] = useState<string>(undefined)

  const [step, setStep] = useState<'enter' | 'verify'>('enter')
  const [code, setCode] = useState<string>("")
  const [verifyError, setVerifyError] = useState<string>(undefined)
  const [verifying, setVerifying] = useState<boolean>(false)
  const [successVisible, setSuccessVisible] = useState<boolean>(false)

  const navigation = useNavigation()

  const BULLET_POINT = "\u2022"
  const THREE_BULLETS = BULLET_POINT.repeat(3)

  const isPhoneValid = !!phone && PHONE_REGEX.test(phone)

  const handleSendCode = async () => {
    setError(undefined)
    const target = `${countryCode.split(' ')[1]}${phone}`
    if (!phone || !PHONE_REGEX.test(phone)) {
      setError('Invalid phone number format')
      return
    }
    setSending(true)
    try {
      await api.triggerUserMFA(target)
      setStep('verify')
    } catch (e) {
      setError(e.message || 'An error occurred while sending the code')
    } finally {
      setSending(false)
    }
  }

  const handleVerify = async () => {
    setVerifyError(undefined)
    setVerifying(true)
    let succeeded = false
    try {
      await api.verifyUserMFA(code)
      setSuccessVisible(true)
      succeeded = true
    } catch (e) {
      setVerifyError(e.message || 'Invalid verification code')
    } finally {
      setVerifying(false)
      Analytics.track('mfa_verified', {
        success: succeeded,
      })
    }
  }

  return (
    <>
    <DismissKeyboard>
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 16, backgroundColor: bgMain }}>
        <KeyboardAvoidingView
          style={{ gap: 16, width: '100%', maxWidth: 700, alignItems: 'center' }}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 100 : 0}
        >
          {step === 'enter' ? (
            <>
              <MaterialCommunityIcons name="shield-edit" color={textMain} size={150}/>
              <Text style={[textStyles.h3, { color: textMain, textAlign: 'center' }]}>Enter the new MFA phone number</Text>
              <Text style={[textStyles.body, { color: textMain, textAlign: 'center' }]}>
                We&apos;ll send a verification code to the new phone number to confirm{' '}
                you have access to this number.
              </Text>
              <View style={{ width: '100%' }}>
                <PhoneNumberInput
                  inputRef={inputRef}
                  value={{ countryCode, phoneNumber: phone }}
                  onChange={(cc, p) => {
                    setCountryCode(cc)
                    setPhone(p)
                  }}
                  placeholder="Phone Number"
                  error={error}
                />
              </View>
              
              <ThemedButton
                text="Send Verification Code"
                onPress={() => {
                  Analytics.track('mfa_send_code_pressed')
                  handleSendCode()
                }}
                loading={sending}
                disabled={sending || !isPhoneValid}
                variant="primary"
              />
            </>
          ) : (
            <>
              <MaterialCommunityIcons name="shield-key" color={textMain} size={150}/>
              <Text style={[textStyles.h3, { color: textMain, textAlign: 'center' }]}>
                Confirmation Code
              </Text>
              <Text style={[textStyles.body, { color: textMain, textAlign: 'center' }]}>
                Please provide the verification code we sent to{' '}
                {THREE_BULLETS}{phone.substring(phone.length - 4)}{' '}
              </Text>
              <View style={{ width: '100%', alignItems: 'center' }}>
                <MFAInput
                  inputRef={inputRef as any}
                  value={code}
                  onChangeText={setCode}
                  onSubmitEditing={handleVerify}
                  error={verifyError}
                />
              </View>
              <ThemedButton
                text="Verify"
                onPress={handleVerify}
                loading={verifying}
                disabled={verifying || code?.length !== 6}
                variant="primary"
              />
            </>
          )}
        </KeyboardAvoidingView>
      </View>
    </DismissKeyboard>
    <ThemedDialog
      visible={successVisible}
      title="MFA Updated"
      subMessage="Your MFA phone number was successfully updated."
      options={[
        {
          title: 'Okay',
          onPress: () => {
            setSuccessVisible(false)
            navigation.dispatch(StackActions.pop(1))
          },
          role: 'confirm',
          icon: <Ionicons name="checkmark" size={22} />
        }
      ]}
    />
    </>
  )
}

export default ManageMfa


